import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Locale } from '../../modules/locale'
import { ConsoleError } from '../../modules/logging'
import MainModule from '../../modules/main'
import { AbstractSearchCore, AbstractSubscriber, IFacet } from '../../modules/search'

interface LVCTag {
  facet: 'uiAdditionalTypesCode' | 'audience' // used to identify the facet (for now uiAdditionalTypesCode | audience)
  label: {
    fr: string
    en: string
  }
  facetValue: string | string[] // used to identify the facet value (for now the code of the facet);
}
class TagSubscriber extends AbstractSubscriber {
  public _core: AbstractSearchCore | null = null

  constructor(groupId: string = '') {
    super(groupId)
  }

  async attached(core: AbstractSearchCore) {
    this._core = core
  }

  async detaching() {}

  async onFacetUpdate() {}

  async onSearchConfigUpdate() {}

  async onResultUpdate() {}

  async onSearchTextUpdate() {}
}
export class LvcTags extends MinzeElement {
  attrs: Attrs = [
    'data-search-modal-target',
    'data-discipline-tags',
    'data-audience-tags',
    'data-size',
    ['data-layout', 'block'],
  ]
  reactive: Reactive = [['tagItems', []]]
  decodedDisciplineTags: any = null
  decodedAudienceTags: string[] | string | null = null
  /**
   * Splits the label string and returns the first.
   * If the label is null or undefined, returns label.
   * @param label - The label string to split.
   * @returns The first part of the label string.
   */
  splitLabel(label: string): string {
    return label?.split(' > ')[0] ?? label
  }
  textListToArray = (text: string) => {
    return text.split(',')
  }

  /**
   * Maps the audience value to a corresponding label and facet value. Used for rendering the tags and data attributes.
   *
   * @param audience - The audience value to be mapped.
   * @returns An object containing the label and facet value.
   */
  mapAudience(audience: string) {
    switch (audience) {
      case 'General':
        return {
          label: { fr: 'Général', en: 'General' },
          facetValue: 'General',
        }
      case 'Adult': {
        return {
          label: { fr: 'Adulte', en: 'Adult' },
          facetValue: 'Adult',
        }
      }
      case 'Family':
      case 'Families': {
        return {
          label: { fr: 'Familial', en: 'Family' },
          facetValue: 'Families',
        }
      }
      case 'With Baby':
      case 'With baby': {
        return {
          label: { fr: 'Famille avec bébé', en: 'Family with baby' },
          facetValue: 'With baby',
        }
      }
      case 'Childhood':
      case 'Children': {
        return {
          label: { fr: 'Enfant', en: 'Children' },
          facetValue: ['Children', 'Childhood'],
        }
      }
      case 'Toddler': {
        return {
          label: { fr: 'Tout-Petit', en: 'Toddler' },
          facetValue: 'Toddler',
        }
      }
      case 'Youth':
      case 'Youths': {
        return {
          label: { fr: 'Adolescent', en: 'Youth' },
          facetValue: 'Youths',
        }
      }
      case 'Elder':
      case 'Elders': {
        return {
          label: { fr: 'Aîné', en: 'Elder' },
          facetValue: ['Elders', 'Adulte'],
        }
      }
      default: {
        return {
          label: { fr: audience, en: audience },
          facetValue: '',
        }
      }
    }
  }

  /**
   * Handles the click event for the tag buttons. Takes the data-facet and data-facet-value attributes and uses them to create a new facet object. Then it subscribes to the search manager and applies the new facet to the search config.
   */
  onClick = (e: Event) => {
    if (!e.target) return
    const tagSubscriber = new TagSubscriber()
    MainModule.getInstance().getSearchManager().subscribe(tagSubscriber)
    const target = e.target as HTMLElement
    const tag = target.closest('.tag') as HTMLElement
    const facet = tag.getAttribute('data-facet')
    const facetValue = tag.getAttribute('data-facet-value')
    if (!facet || !facetValue) {
      return
    }

    const facetValueDecoded = JSON.parse(decodeURIComponent(facetValue)) as string | string[]
    const facetObj: IFacet = {
      name: facet,
      options: Array.isArray(facetValueDecoded)
        ? facetValueDecoded.map((value: string) => {
            return {
              available: true,
              selected: true,
              value: value,
            }
          })
        : [
            {
              available: true,
              selected: true,
              value: facetValueDecoded,
            },
          ],
    }
    let searchConfig = tagSubscriber?._core?.searchConfig

    if (searchConfig) {
      searchConfig.facets = [facetObj]
      if (
        this?.dataSearchModalTarget &&
        (this?.dataSearchModalTarget === 'exhibitions_search' ||
          this?.dataSearchModalTarget === 'events_search')
      ) {
        searchConfig.modal = this?.dataSearchModalTarget
      } else {
        searchConfig.modal = 'events_search'
      }
      tagSubscriber?._core?.applySearchConfig(searchConfig)
      tagSubscriber?._core?.goToSearch()
    }
  }

  onReady() {
    const tagList: LVCTag[] = []
    try {
      this.decodedDisciplineTags = JSON.parse(
        decodeURIComponent(this.dataDisciplineTags as string)
      )?.[0] // Take first object.
    } catch (e) {
      ConsoleError(true, e)
    }
    try {
      this.decodedAudienceTags = JSON.parse(decodeURIComponent(this.dataAudienceTags as string))
    } catch (e) {
      ConsoleError(true, e)
    }
    try {
      // handle edge cases where the tags are sent as a string, separated by commas.
      if (this.decodedAudienceTags && typeof this.decodedAudienceTags === 'string') {
        this.decodedAudienceTags = this.decodedAudienceTags?.split(',')
      }
    } catch (err) {
      ConsoleError(true, err)
    }

    // Combile tag list for rendering, using a splitLabel function to get the first part of the label ex: Music, split on (" > ")
    if (this.decodedDisciplineTags) {
      tagList.push({
        facet: 'uiAdditionalTypesCode',
        label: {
          fr: this.splitLabel(this.decodedDisciplineTags.labelFr),
          en: this.splitLabel(this.decodedDisciplineTags.labelEn),
        },
        facetValue: this.decodedDisciplineTags.code,
      })
    }
    // Combine audience tag list by using a map function to map the audience to the correct label and value
    if (
      this.decodedAudienceTags &&
      this.decodedAudienceTags.length > 0 &&
      Array.isArray(this.decodedAudienceTags)
    ) {
      this.decodedAudienceTags.map((item: string) => {
        const finalItem: LVCTag = {
          facet: 'audience',
          ...this.mapAudience(item),
        }
        tagList.push(finalItem)
      })
    }
    // Remove duplicates from the list (mainly used for audience).
    const tagListCleaned = tagList.filter((item: LVCTag, index: number, self: LVCTag[]) => {
      return (
        index ===
        self.findIndex((t: LVCTag) => {
          return t.label.fr === item.label.fr
        })
      )
    })

    this.tagItems = tagListCleaned
    this.rerender()
  }

  /**
   * Renders the tags based on the tagItems array. Encodes the facet value to be used in the data attribute.
   * @returns {string} The HTML representation of the tags.
   */
  renderTags(): string {
    if (this.tagItems && Array.isArray(this.tagItems) && this.tagItems.length > 0) {
      return this.tagItems
        .map((item: LVCTag) => {
          return `
          <div
            class="tag"
            data-facet="${item.facet}"
            data-facet-value="${encodeURIComponent(JSON.stringify(item.facetValue))}"
          >
            <lvc-tag-icon></lvc-tag-icon>
            <div class="tag-text">${item.label[Locale.language as 'fr' | 'en']}</div>
          </div>
        `
        })
        .join('')
    } else {
      return ''
    }
  }
  html = () => `
    <div class="tags-container">
      ${this.renderTags()}
    </div>
  `
  css = () => `
    :host {
      font-size: 14px;
    }
    .tags-container {
      display: ${this.dataLayout};
      flex-direction: column;
      gap: 0;
    }
    .tag {
      gap: 2px;
      align-items: center;
      text-decoration: none;
      display: flex;
      flex-direction: row;
      cursor: pointer;
      background: none;
      border: none;
      text-weight: 400;
    }
    .tag-text {
      text-decoration: underline;
    }
    @media only screen and (max-width: 768px) {
      .tags-container {
        flex-wrap: wrap;
        flex-direction: row;
        gap: 1rem;
      }
    }
  `

  eventListeners: EventListeners = [['.tag', 'click', this.onClick]]
}
